import {engineContext} from './context.js';

(function(global){'use strict';
const VERSION='0.1.0-alpha';

function clone(x){ return x==null?x:JSON.parse(JSON.stringify(x)); }

function makeMemoryDriver(){
  const store=new Map();
  return Object.freeze({
    kind:'MEMORY',
    get(key){ return Promise.resolve(store.has(key)?clone(store.get(key)):null); },
    set(key,value){ store.set(key,clone(value)); return Promise.resolve(true); },
    remove(key){ const existed=store.delete(key); return Promise.resolve(existed); },
    keys(){ return Promise.resolve([...store.keys()].sort()); },
    clear(){ store.clear(); return Promise.resolve(true); }
  });
}

function makeLocalStorageDriver(storage){
  const s=storage||(typeof localStorage!=='undefined'?localStorage:null);
  return Object.freeze({
    kind:'LOCAL_STORAGE',
    get(key){
      if(!s) return Promise.reject(new Error('LOCAL_STORAGE_UNAVAILABLE'));
      const raw=s.getItem(key);
      if(raw==null) return Promise.resolve(null);
      try{return Promise.resolve(JSON.parse(raw));}
      catch(e){return Promise.reject(new Error('LOCAL_STORAGE_PARSE_ERROR'));}
    },
    set(key,value){
      if(!s) return Promise.reject(new Error('LOCAL_STORAGE_UNAVAILABLE'));
      s.setItem(key,JSON.stringify(value)); return Promise.resolve(true);
    },
    remove(key){
      if(!s) return Promise.reject(new Error('LOCAL_STORAGE_UNAVAILABLE'));
      const existed=s.getItem(key)!==null; s.removeItem(key); return Promise.resolve(existed);
    },
    keys(){
      if(!s) return Promise.reject(new Error('LOCAL_STORAGE_UNAVAILABLE'));
      const out=[]; for(let i=0;i<s.length;i++) out.push(s.key(i));
      return Promise.resolve(out.filter(Boolean).sort());
    }
  });
}

function createRepository(opts){
  opts=opts||{};
  const driver=opts.driver||makeMemoryDriver();
  const adapter=opts.adapter||global.ZooInvestResearchStatePersistenceAdapter||null;
  if(!adapter) throw new Error('PERSISTENCE_ADAPTER_REQUIRED');

  function key(symbol,scope){ return adapter.storageKey(symbol,scope); }

  async function load(symbol,scope){
    const k=key(symbol,scope),raw=await driver.get(k);
    if(!raw) return Object.freeze({found:false,key:k,record:null});
    const migrated=adapter.migrate(raw);
    if(!migrated.migrated) return Object.freeze({found:true,key:k,record:null,error:migrated.reason});
    const valid=adapter.validateRecord(migrated.record);
    if(!valid.valid) return Object.freeze({found:true,key:k,record:null,error:'INVALID_RECORD',issues:valid.issues});
    return Object.freeze({found:true,key:k,record:Object.freeze(clone(migrated.record))});
  }

  async function save(input,options){
    options=options||{};
    const packed=adapter.pack(input);
    const valid=adapter.validateRecord(packed);
    if(!valid.valid) return Object.freeze({saved:false,error:'INVALID_RECORD',issues:valid.issues});

    const k=key(packed.symbol,packed.scope);
    const current=await driver.get(k);

    if(options.expectedSavedAt && current && current.savedAt!==options.expectedSavedAt){
      return Object.freeze({
        saved:false,
        error:'WRITE_CONFLICT',
        key:k,
        expectedSavedAt:options.expectedSavedAt,
        actualSavedAt:current.savedAt||null
      });
    }

    await driver.set(k,packed);
    return Object.freeze({saved:true,key:k,record:Object.freeze(clone(packed))});
  }

  async function remove(symbol,scope){
    const k=key(symbol,scope);
    return Object.freeze({removed:await driver.remove(k),key:k});
  }

  async function compare(symbol,scope,nextInput){
    const existing=await load(symbol,scope);
    const next=adapter.pack(nextInput);
    return Object.freeze({
      found:existing.found,
      diff:adapter.diff(existing.record||{},next),
      current:existing.record,
      next
    });
  }

  return Object.freeze({VERSION,driverKind:driver.kind,key,load,save,remove,compare});
}

async function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  const adapter={
    storageKey:(s,c)=>`k:${c||'RESEARCH'}:${s||'UNKNOWN'}`,
    pack:(x)=>({
      schemaVersion:'1',recordType:'ZOOINVEST_RESEARCH_STATE',
      symbol:x.symbol||null,scope:x.scope||'RESEARCH',
      savedAt:x.savedAt||'T0',
      evidenceFingerprint:x.evidenceFingerprint||null,
      thesisFingerprint:x.thesisFingerprint||null,
      completionMemory:Array.isArray(x.completionMemory)?x.completionMemory:[],
      openGaps:Array.isArray(x.openGaps)?x.openGaps:[],
      invalidations:Array.isArray(x.invalidations)?x.invalidations:[]
    }),
    validateRecord:(r)=>({valid:!!(r&&r.symbol&&r.savedAt&&r.recordType==='ZOOINVEST_RESEARCH_STATE'),issues:[]}),
    migrate:(r)=>({migrated:r&&r.schemaVersion==='1',record:r,reason:r&&r.schemaVersion==='1'?'CURRENT_SCHEMA':'UNSUPPORTED_SCHEMA_VERSION'}),
    diff:(a,b)=>({evidenceChanged:(a.evidenceFingerprint||null)!==(b.evidenceFingerprint||null)})
  };

  const d=makeMemoryDriver();
  const repo=createRepository({driver:d,adapter});

  const miss=await repo.load('AAA','OWNED');
  ok('missing record returns found false',miss.found===false);

  const s1=await repo.save({symbol:'AAA',scope:'OWNED',savedAt:'T1',evidenceFingerprint:'e1'});
  ok('valid record saves',s1.saved===true);

  const l1=await repo.load('AAA','OWNED');
  ok('saved record loads',l1.found===true&&l1.record.symbol==='AAA');
  ok('scope preserved',l1.record.scope==='OWNED');

  const cmp=await repo.compare('AAA','OWNED',{symbol:'AAA',scope:'OWNED',savedAt:'T2',evidenceFingerprint:'e2'});
  ok('compare detects evidence change',cmp.diff.evidenceChanged===true);

  const conflict=await repo.save(
    {symbol:'AAA',scope:'OWNED',savedAt:'T3',evidenceFingerprint:'e3'},
    {expectedSavedAt:'WRONG'}
  );
  ok('optimistic write conflict detected',conflict.saved===false&&conflict.error==='WRITE_CONFLICT');

  const goodUpdate=await repo.save(
    {symbol:'AAA',scope:'OWNED',savedAt:'T4',evidenceFingerprint:'e4'},
    {expectedSavedAt:'T1'}
  );
  ok('matching optimistic token allows save',goodUpdate.saved===true);

  const l2=await repo.load('AAA','OWNED');
  ok('updated record persisted',l2.record.savedAt==='T4');

  const rm=await repo.remove('AAA','OWNED');
  ok('remove deletes existing record',rm.removed===true);

  const miss2=await repo.load('AAA','OWNED');
  ok('removed record is gone',miss2.found===false);

  await d.set('bad',{schemaVersion:'99'});
  ok('memory driver stores arbitrary test value',(await d.get('bad')).schemaVersion==='99');

  const keys=await d.keys();
  ok('memory driver keys are deterministic',keys.join(',')==='bad');

  await d.clear();
  ok('memory driver clear works',(await d.keys()).length===0);

  const fakeStorage=(()=>{
    const m=new Map();
    return {
      get length(){return m.size;},
      key(i){return [...m.keys()][i]??null;},
      getItem(k){return m.has(k)?m.get(k):null;},
      setItem(k,v){m.set(k,String(v));},
      removeItem(k){m.delete(k);}
    };
  })();
  const ld=makeLocalStorageDriver(fakeStorage);
  await ld.set('x',{a:1});
  ok('local storage driver roundtrip works',(await ld.get('x')).a===1);
  ok('local storage driver reports kind',ld.kind==='LOCAL_STORAGE');

  ok('repository exposes no trade signal',!('tradeSignal' in repo));
  ok('repository exposes no capital permission',!('capitalPermission' in repo));

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={VERSION,makeMemoryDriver,makeLocalStorageDriver,createRepository,selfTest};
global.ZooInvestResearchStateRepositoryEngine=Object.freeze(API);
})(engineContext);